import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {assetNameDuplicate, getAsset, update} from "../../../../api/asset/AssetService";
import {getCodes} from "../../../../api/code/CodeService";
import {getCustomCodeList, getCustomStringList, getGroupSelectList} from "../../../../api/field/CustomCodeService";
import {getFields} from "../../../../api/field/FieldService";
import {error, apiLoading} from "../../../../redux/slice/apiSlice";
import {checkDuplicate, getFlatCode, isMobile} from "../../../../components/commonmethod/CommonMethod";
import {CacheType} from "../../../../constant/CodeSpecNum";
import {useCacheRefresh} from "../../../../hooks/useCacheRefresh";
import {handleActiveError, handleActiveToast} from "../../../../components/sonner/ToastBasic";
import EditAttachPanel from "../../../../components/files/EditAttachPanel";
import {
    buildAssetRequestDto,
    getPendingUploadFiles,
    loadAttachState,
} from "../../../../components/files/editAttachState";
import {
    buildCommonInfoFieldDefs,
    buildMaintenanceFieldDefs,
    chunkFieldsIntoRows,
    CommonInfoPanel,
    fieldsToKvCells,
    MaintenancePanel,
} from "./shared/assetDetailKv";
import {useAssetInfoEdit} from "./shared/useAssetInfoEdit";
import {ContentDetailTwoColLayout, DetailFormPageShell, DetailListPage, DetailSummaryButton} from "../../../../components/detailLayout";
import "./subPages/view/assetInfo.css";
import "../../../../components/detailLayout/detailListLayout.css";

function AssetUpdate() {
    const {id} = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const platFrom = useSelector((state) => state.auth.platFrom);
    const refreshCache = useCacheRefresh();
    const initialAssetNameRef = useRef("");

    const [asset, setAsset] = useState();
    const [flat, setFlat] = useState();
    const [customCodes, setCustomCodes] = useState([]);
    const [customStrings, setCustomStrings] = useState([]);
    const [fields, setFields] = useState([]);
    const [groupSelect, setGroupSelect] = useState([]);
    const [attachState, setAttachState] = useState(null);

    const {fieldList, renderEdit} = useAssetInfoEdit({
        asset,
        setAsset,
        flat,
        fields,
        customCodes,
        groupSelect,
        renewGroupFields: () => getGroupSelectList().then((res) => setGroupSelect(res.data)),
    });

    useEffect(() => {
        setAsset(undefined);
        setAttachState(null);

        Promise.all([
            getFields(),
            getAsset(id),
            loadAttachState("asset", id, {withImages: false}),
            getCodes(),
            getCustomCodeList(),
            getCustomStringList(),
            getGroupSelectList(),
        ])
            .then(([fieldsRes, assetRes, attach, codesRes, ccRes, csRes, groupRes]) => {
                if (assetRes.status === 200) {
                    if (assetRes.data.auth?.update === false) {
                        alert("수정 권한이 없습니다.");
                        navigate(`/asset/${id}`);
                        return;
                    }
                    setAsset(assetRes.data);
                    initialAssetNameRef.current = assetRes.data.assetName ?? "";
                    setAttachState(attach);
                } else if (assetRes.status === 201) {
                    if (assetRes.data.errorCode === "AUTHORITY") {
                        alert(assetRes.data.reason);
                    }
                    navigate(`/asset/${id}`);
                    return;
                }

                setFields(fieldsRes.data);
                setFlat(getFlatCode(codesRes.data.content));
                setCustomCodes(ccRes.data);
                setCustomStrings(csRes.data);
                setGroupSelect(groupRes.data);
            })
            .catch((err) => {
                dispatch(error({code: err.response?.status || 500}));
            });
    }, [id, dispatch, navigate]);

    const commonInfoRows = useMemo(() => {
        if (!asset) return [];
        const defs = buildCommonInfoFieldDefs(asset, platFrom, customCodes, customStrings);
        return chunkFieldsIntoRows(defs).map((rowDefs, index) => {
            const cells = fieldsToKvCells(rowDefs, true, renderEdit);
            const key = rowDefs.map((d) => d.editArgs?.[0]?.id ?? d.editKey ?? d.label).join("-");
            return {key: key || `common-row-${index}`, cells};
        });
    }, [asset, platFrom, customCodes, customStrings, renderEdit]);

    const maintenanceRows = useMemo(() => {
        if (!asset) return [];
        const defs = buildMaintenanceFieldDefs(asset, fieldList);
        return chunkFieldsIntoRows(defs).map((rowDefs, index) => {
            const cells = fieldsToKvCells(rowDefs, true, renderEdit);
            const key = rowDefs.map((d) => d.editArgs?.[0]?.id ?? d.editKey ?? d.label).join("-");
            return {
                key: key || `maint-row-${index}`,
                cells,
                rowAuto: rowDefs.some((d) => d.rowAuto),
            };
        });
    }, [asset, fieldList, renderEdit]);

    const cancel = () => {
        navigate(`/asset/${id}`);
    };

    const save = async () => {
        if (!asset?.assetName?.trim()) {
            alert("필수값이 누락되었습니다.");
            return;
        }

        if (asset.assetName !== initialAssetNameRef.current) {
            const duplicate = await checkDuplicate("수정할 수 없습니다.", asset.assetName, assetNameDuplicate);
            if (duplicate) return;
        }

        if (!window.confirm("변경 내용을 저장하시겠습니까?")) {
            return;
        }

        const {images, files} = getPendingUploadFiles(attachState);
        const requestDto = buildAssetRequestDto(asset, attachState);

        dispatch(apiLoading(true));
        try {
            const response = await update(asset.id, requestDto, images, files);
            if (response.status === 200) {
                handleActiveToast({
                    description: "",
                    message: "수정 되었습니다.",
                    labelListener: () => {},
                });
                refreshCache(CacheType.asset);
                navigate(`/asset/${id}`);
            } else if (response.status === 201 || response.status === 202) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            } else if (response.status === 413) {
                handleActiveError({message: "첨부파일 용량이 너무 큽니다."});
            } else {
                handleActiveError({message: "처리실패", description: "알 수 없는 에러"});
            }
        } finally {
            dispatch(apiLoading(false));
        }
    };

    if (!asset || !flat || !attachState) {
        return null;
    }

    const summaryMeta = [
        asset.assetCode && `코드 ${asset.assetCode}`,
        asset.group?.name && `그룹 ${asset.group.name}`,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <DetailFormPageShell
            title={asset.assetName || "자산 수정"}
            className={`common-container ${isMobile ? "" : "container-height"} detail-list-layout asset-detail-list-layout asset-detail-edit-page border-l-gray`}
            meta={summaryMeta || undefined}
            actions={
                <>
                    <DetailSummaryButton onClick={save}>
                        저장
                    </DetailSummaryButton>
                    <DetailSummaryButton muted className="ml-1" onClick={cancel}>
                        취소
                    </DetailSummaryButton>
                </>
            }
        >
            <DetailListPage isEditing className="flex min-h-0 flex-1 flex-col">
                <div className={`container-height-body asset-detail-body detail-list-body flex min-h-0 flex-1 flex-col ${isMobile ? "" : "overflow-hidden"}`}>
                    <ContentDetailTwoColLayout
                        main={
                            <>
                                <CommonInfoPanel rows={commonInfoRows} isEditing />
                                <MaintenancePanel rows={maintenanceRows} isEditing />
                            </>
                        }
                        aside={<EditAttachPanel attachState={attachState} setAttachState={setAttachState} />}
                    />
                </div>
            </DetailListPage>
        </DetailFormPageShell>
    );
}

export default AssetUpdate;
